import { useRef, useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Check } from "lucide-react";
import { VideoAvatarAvailableFacesResponseDto } from "@packages/api/madlenAPI.schemas";

const AvatarSelector = ({
  avatars,
  input,
  setInput,
}: {
  avatars: VideoAvatarAvailableFacesResponseDto[];
  input: {
    requestAvatar?: VideoAvatarAvailableFacesResponseDto | undefined;
  };
  setInput: (input: {
    requestAvatar: VideoAvatarAvailableFacesResponseDto | undefined;
  }) => void;
}) => {
  // Video references for each avatar
  const videoRefs = useRef<(HTMLVideoElement | null)[]>([]);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [endedVideos, setEndedVideos] = useState<Set<number>>(new Set());

  // Function to stop all videos except the specified index
  const stopAllVideosExcept = (exceptIndex?: number) => {
    videoRefs.current.forEach((video, index) => {
      if (video && index !== exceptIndex) {
        video.pause();
        video.currentTime = 0;
        setEndedVideos((prev) => {
          const updated = new Set(prev);
          updated.add(index);
          return updated;
        });
      }
    });
  };

  // Play video when avatar is selected
  useEffect(() => {
    const selectedIndex = avatars.findIndex(
      (avatar) => avatar.avatarId === input.requestAvatar?.avatarId,
    );

    if (selectedIndex !== -1) {
      // Stop all other videos when one is selected
      stopAllVideosExcept(selectedIndex);

      const video = videoRefs.current[selectedIndex];
      if (video) {
        video.pause();
        video.currentTime = 0;
        setEndedVideos((prev) => {
          const updated = new Set(prev);
          updated.delete(selectedIndex);
          return updated;
        });
        video
          .play()
          .catch((error) => console.error("Failed to autoplay video:", error));
      }
    } else {
      // If no avatar is selected, stop all videos
      stopAllVideosExcept();
    }
  }, [input.requestAvatar, avatars]);

  // Handle video playback
  const playVideo = (index: number) => {
    const video = videoRefs.current[index];
    if (video) {
      try {
        video.pause();
        video.currentTime = 0;
        video.muted = false;
        setEndedVideos((prev) => {
          const updated = new Set(prev);
          updated.delete(index);
          return updated;
        });
        video.play();
      } catch (error) {
        console.error("Video play failed:", error);
      }
    }
  };

  const handleVideoEnded = (index: number) => {
    setEndedVideos((prev) => {
      const updated = new Set(prev);
      updated.add(index);
      return updated;
    });
  };

  const getResolutionUrl = (
    url: string | undefined,
    type: "image" | "video",
  ) => {
    // Return early if no URL is provided
    if (!url) return "";

    const extension = type === "video" ? ".mp4" : /\.(jpg|jpeg|png|webp)$/;
    const resolutionUrl = url.replace(
      extension,
      `_medium${type === "video" ? ".mp4" : ".$1"}`,
    );

    return resolutionUrl;
  };

  const handleAvatarClick = (
    avatar: VideoAvatarAvailableFacesResponseDto,
    index: number,
  ) => {
    // If this avatar is already selected, deselect it
    if (input.requestAvatar?.avatarId === avatar.avatarId) {
      setInput({ ...input, requestAvatar: undefined });

      // Stop the currently playing video
      const video = videoRefs.current[index];
      if (video) {
        video.pause();
        video.currentTime = 0;
        handleVideoEnded(index);
      }

      console.log("Deselected avatar:", avatar.avatarId);
      return;
    }

    // Set the new avatar selection
    setInput({ ...input, requestAvatar: avatar });

    // Stop all other videos immediately
    stopAllVideosExcept(index);

    // Play the selected avatar's video
    playVideo(index);

    console.log("Selected avatar:", avatar.avatarId);
  };

  const handleMouseEnter = (index: number) => {
    // Allow hover effects when no avatar is selected
    if (!input.requestAvatar) {
      setHoveredIndex(index);
      playVideo(index);
    }
  };

  const handleMouseLeave = (index: number) => {
    // Handle mouse leave when no avatar is selected
    if (!input.requestAvatar) {
      setHoveredIndex(null);
      const video = videoRefs.current[index];
      if (video) {
        video.pause();
        video.currentTime = 0;
        handleVideoEnded(index);
      }
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-5 gap-4"
    >
      {avatars.map((avatar, index) => {
        const isSelected = input.requestAvatar?.avatarId === avatar.avatarId;
        const isHovered = hoveredIndex === index && !input.requestAvatar;
        const isVideoEnded = endedVideos.has(index);
        const shouldShowVideo = (isSelected || isHovered) && !isVideoEnded;

        return (
          <motion.div
            key={index}
            layout
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{
              type: "spring",
              stiffness: 300,
              damping: 20,
              delay: index * 0.05,
            }}
            whileHover={{
              scale: 1.05,
              transition: { type: "spring", stiffness: 400, damping: 10 },
            }}
            onClick={() => handleAvatarClick(avatar, index)}
            onMouseEnter={() => handleMouseEnter(index)}
            onMouseLeave={() => handleMouseLeave(index)}
            className={`
              group relative cursor-pointer rounded-2xl overflow-hidden aspect-[1/2]
              ${
                isSelected
                  ? "ring-4 ring-purple-500 shadow-xl after:absolute after:inset-0 after:rounded-2xl after:ring-2 after:ring-purple-300 after:blur-md after:opacity-40"
                  : "hover:ring-2 hover:ring-purple-300 dark:hover:ring-purple-600"
              }
              transition-all duration-300
            `}
          >
            <AnimatePresence>
              {isSelected && (
                <motion.span
                  initial={{ opacity: 0, scale: 0 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0 }}
                  transition={{ type: "spring", stiffness: 500, damping: 15 }}
                  className="absolute top-2 right-2 text-white bg-purple-600 rounded-full w-8 h-8 shadow-lg z-10 flex items-center justify-center text-center"
                >
                  <motion.div
                    animate={{
                      y: [0, -4, 0],
                    }}
                    transition={{
                      repeat: Infinity,
                      duration: 1.5,
                      ease: "easeInOut",
                    }}
                  >
                    <Check size={20} />
                  </motion.div>
                </motion.span>
              )}
            </AnimatePresence>

            {/* Static portrait */}
            <motion.div className="w-full h-full">
              <motion.img
                src={getResolutionUrl(avatar.sampleImageUrl, "image")}
                alt={`Avatar ${index + 1}`}
                className="w-full h-full object-cover absolute inset-0"
                initial={{ opacity: 1 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.3 }}
              />
            </motion.div>

            {/* Talking video */}
            <motion.video
              ref={(el) => (videoRefs.current[index] = el)}
              src={getResolutionUrl(avatar.sampleVideoUrl, "video")}
              className="absolute inset-0 w-full h-full object-cover z-[2]"
              initial={{ opacity: 0 }}
              animate={{
                opacity: shouldShowVideo ? 1 : 0,
              }}
              transition={{ duration: 0.3 }}
              playsInline
              preload="auto"
              loop={isHovered && !isSelected} // Only loop on hover if not selected
              disablePictureInPicture
              onEnded={() => handleVideoEnded(index)}
            />

            {/* Highlight effect for selected avatar */}
            {isSelected && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 0.15 }}
                className="absolute inset-0 bg-purple-300 dark:bg-purple-700 mix-blend-overlay z-[1]"
              />
            )}

            {/* Avatar name */}
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-2 z-[3]">
              <p className="text-white text-sm font-medium truncate text-center">
                {avatar.avatarName || `Avatar ${index + 1}`}
              </p>
            </div>
          </motion.div>
        );
      })}
    </motion.div>
  );
};

export default AvatarSelector;
